import { useCallback } from 'react';
import { useDispatch, useSelector } from 'store';
import { reduxActions } from 'store';
import { IWiseTableQueryParams } from 'components/wise/types';

interface IUseWiseTableReduxSyncParams {
  pageSymbol?: string;
  componentSymbol: string;
}

interface IUseWiseTableReduxSyncReturn {
  syncToRedux: (queryParams: IWiseTableQueryParams) => void;
  getInitialParams: () => IWiseTableQueryParams | undefined;
  isDynamicUI: boolean;
  initialParams: IWiseTableQueryParams | undefined;
}

/**
 * Hook do synchronizacji queryParams z Redux
 */
export const useWiseTableReduxSync = ({
  pageSymbol,
  componentSymbol
}: IUseWiseTableReduxSyncParams): IUseWiseTableReduxSyncReturn => {
  const dispatch = useDispatch();

  // Wybór slice w zależności od tego, czy pageSymbol jest podany
  const isDynamicUI = !!pageSymbol;

  // Odczytywanie z odpowiedniego slice
  const dynamicPageContextFilters = useSelector((state) => (isDynamicUI ? state.dynamicPage?.contextFilters : null));
  const traderPanelParameters = useSelector((state) => (!isDynamicUI ? state.traderPanel?.parameters : null));

  /**
   * Synchronizuje queryParams z odpowiednim slice'em w Redux
   */
  const syncToRedux = useCallback(
    (queryParams: IWiseTableQueryParams) => {
      if (isDynamicUI && pageSymbol) {
        dispatch(
          reduxActions.setContextFilters({
            [pageSymbol]: { [componentSymbol]: queryParams }
          })
        );
      } else if (!isDynamicUI) {
        dispatch(
          reduxActions.setTraderPanelParameters({
            componentSymbol,
            parameters: queryParams
          })
        );
      }
    },
    [isDynamicUI, pageSymbol, componentSymbol, dispatch]
  );

  /**
   * Odczytuje queryParams z odpowiedniego slice'a w Redux
   */
  const getInitialParams = (): IWiseTableQueryParams | undefined => {
    if (isDynamicUI && pageSymbol && dynamicPageContextFilters?.[pageSymbol]?.[componentSymbol]) {
      return dynamicPageContextFilters[pageSymbol][componentSymbol] as IWiseTableQueryParams;
    }

    if (!isDynamicUI && traderPanelParameters?.[componentSymbol]) {
      const params = traderPanelParameters[componentSymbol];
      if (params.page !== undefined && params.limit !== undefined) {
        return params as IWiseTableQueryParams;
      }
    }

    return undefined;
  };

  // Obliczamy initialParams raz, żeby mieć aktualną wartość
  const initialParams = getInitialParams();

  return {
    syncToRedux,
    getInitialParams,
    isDynamicUI,
    initialParams
  };
};
